import { useCallback } from 'react';
import { GameState, Move } from '../services/gameEngineApi';
import { LegacyGameState } from './useLegacyGameState';

interface UseGameInteractionProps {
  backendGameState: GameState | null;
  legacyGameState: LegacyGameState;
  availableMoves: Move[];
  movePiece: (
    playerIndex: number,
    pieceIndex: number,
    diceValue: number
  ) => Promise<void>;
  setError: (error: string | null) => void;
}

export const useGameInteraction = ({
  backendGameState,
  legacyGameState,
  availableMoves,
  movePiece,
  setError,
}: UseGameInteractionProps) => {
  // Handle piece click
  const handlePieceClick = useCallback(
    (pieceIndex: number) => {
      if (!backendGameState || !legacyGameState.diceValue) {
        console.log('Cannot move piece: no dice value or game state');
        return;
      }

      const currentPlayerIndex = legacyGameState.currentPlayer;
      const currentPlayer = legacyGameState.players[currentPlayerIndex];

      if (!currentPlayer) {
        console.log('No current player found');
        return;
      }

      // Check if this piece can move
      const canMove = legacyGameState.validMoves.includes(pieceIndex);

      if (!canMove) {
        console.log('Piece cannot move');
        setError('This piece cannot move with the current dice value');
        return;
      }

      // Execute the move
      movePiece(currentPlayerIndex, pieceIndex, legacyGameState.diceValue);
    },
    [backendGameState, legacyGameState, movePiece, setError]
  );

  // Handle square click (for moving pieces to specific positions)
  const handleSquareClick = useCallback(
    (position: number) => {
      if (!backendGameState || !legacyGameState.diceValue) {
        console.log('Cannot move to square: no dice value or game state');
        return;
      }

      const currentPlayerIndex = legacyGameState.currentPlayer;
      const currentPlayer = legacyGameState.players[currentPlayerIndex];

      if (!currentPlayer) {
        console.log('No current player found');
        return;
      }

      // Find a piece that can move to this position
      const targetPosition = position;
      const diceValue = legacyGameState.diceValue;

      for (
        let pieceIndex = 0;
        pieceIndex < currentPlayer.pieces.length;
        pieceIndex++
      ) {
        const piece = currentPlayer.pieces[pieceIndex];
        const expectedPosition = piece.position + diceValue;

        if (
          expectedPosition === targetPosition &&
          legacyGameState.validMoves.includes(pieceIndex)
        ) {
          movePiece(currentPlayerIndex, pieceIndex, diceValue);
          return;
        }
      }

      console.log('No valid piece can move to this position');
      setError('No piece can move to this position');
    },
    [backendGameState, legacyGameState, movePiece, setError]
  );

  // Check if current player can make a move
  const canCurrentPlayerMove = useCallback(() => {
    return (
      legacyGameState.validMoves.length > 0 &&
      legacyGameState.diceValue !== null
    );
  }, [legacyGameState.validMoves, legacyGameState.diceValue]);

  // Get available moves for current player
  const getCurrentPlayerMoves = useCallback(() => {
    if (!backendGameState) return [];

    const currentPlayerId =
      backendGameState.players[backendGameState.currentPlayerIndex]?.id;
    return availableMoves.filter((move) => move.playerId === currentPlayerId);
  }, [backendGameState, availableMoves]);

  return {
    handlePieceClick,
    handleSquareClick,
    canCurrentPlayerMove,
    getCurrentPlayerMoves,
  };
};
